import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with interceptors
const apiClient = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !window.location.pathname.startsWith('/login')) {
      localStorage.removeItem('wahr_token');
      localStorage.removeItem('wahr_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ============ Auth API ============

export const authAPI = {
  login: async (email, password) => {
    const response = await apiClient.post('/auth/login', { email, password });
    return response.data;
  },
  
  register: async (name, email, password) => {
    const response = await apiClient.post('/auth/register', { name, email, password });
    return response.data;
  },
  
  getMe: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },
  
  logout: async () => {
    const response = await apiClient.post('/auth/logout');
    return response.data;
  }
};

// ============ Users API (Admin) ============

export const usersAPI = {
  getAll: async () => {
    const response = await apiClient.get('/users');
    return response.data;
  },
  
  getOne: async (userId) => {
    const response = await apiClient.get(`/users/${userId}`);
    return response.data;
  },
  
  updateProfile: async (userId, profileData) => {
    const response = await apiClient.put(`/users/${userId}/profile`, profileData);
    return response.data;
  },
  
  updateSettings: async (userId, settings) => {
    const response = await apiClient.put(`/users/${userId}/settings`, settings);
    return response.data;
  },
  
  createTrainer: async (data) => {
    const response = await apiClient.post('/users/create-trainer', data);
    return response.data;
  },
  
  updateCredentials: async (userId, data) => {
    const response = await apiClient.put(`/users/${userId}/credentials`, data);
    return response.data;
  },
  
  delete: async (userId) => {
    const response = await apiClient.delete(`/users/${userId}`);
    return response.data;
  }
};

// ============ Invoice API ============

export const invoiceAPI = {
  preview: async (userId, campaignId) => {
    const response = await apiClient.get(`/invoices/${userId}/${campaignId}/preview`);
    return response.data;
  },
  
  generate: async (userId, campaignId) => {
    const response = await apiClient.get(`/invoices/${userId}/${campaignId}/generate`, {
      responseType: 'blob'
    });
    return response.data;
  },
  
  download: async (userId, campaignId, trainerName) => {
    const blob = await invoiceAPI.generate(userId, campaignId);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Gutschrift_${trainerName.replace(/ /g, '_')}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
};

// ============ Campaigns API ============

export const campaignsAPI = {
  getAll: async (year) => {
    const params = year ? { year } : {};
    const response = await apiClient.get('/campaigns', { params });
    return response.data;
  },
  
  getCurrent: async () => {
    const response = await apiClient.get('/campaigns/current');
    return response.data;
  },
  
  create: async (data) => {
    const response = await apiClient.post('/campaigns', data);
    return response.data;
  },
  
  update: async (campaignId, data) => {
    const response = await apiClient.put(`/campaigns/${campaignId}`, data);
    return response.data;
  },
  
  delete: async (campaignId) => {
    const response = await apiClient.delete(`/campaigns/${campaignId}`);
    return response.data;
  },
  
  // Move a single trainer's provision to a different campaign (weeks)
  moveTrainerProvision: async (trainerId, sourceCampaignId, targetStartWeek, targetEndWeek, year) => {
    const response = await apiClient.post('/campaigns/move-trainer-provision', {
      trainer_id: trainerId,
      source_campaign_id: sourceCampaignId,
      target_start_week: targetStartWeek,
      target_end_week: targetEndWeek,
      year: year
    });
    return response.data;
  },
  
  // Get all provision backups (Admin only)
  getProvisionBackups: async () => {
    const response = await apiClient.get('/provision-backups');
    return response.data;
  },
  
  // Restore a provision from backup (Admin only)
  restoreProvisionBackup: async (backupId) => {
    const response = await apiClient.post(`/provision-backups/${backupId}/restore`);
    return response.data;
  }
};

// ============ Checklist Template API ============

export const checklistTemplateAPI = {
  get: async () => {
    const response = await apiClient.get('/checklist-template');
    return response.data;
  },
  
  update: async (template) => {
    const response = await apiClient.put('/checklist-template', template);
    return response.data;
  }
};

// ============ Progress API ============

export const progressAPI = {
  get: async (campaignId) => {
    const response = await apiClient.get(`/progress/${campaignId}`);
    return response.data;
  },
  
  update: async (campaignId, checklist) => {
    const response = await apiClient.put(`/progress/${campaignId}`, { checklist });
    return response.data;
  },
  
  getAll: async (campaignId) => {
    const response = await apiClient.get(`/progress/all/${campaignId}`);
    return response.data;
  }
};

// ============ Provisions API ============

export const provisionsAPI = {
  getMine: async (year) => {
    const params = year ? { year } : {};
    const response = await apiClient.get('/provisions', { params });
    return response.data;
  },
  
  getAll: async (year) => {
    const params = year ? { year } : {};
    const response = await apiClient.get('/provisions/all', { params });
    return response.data;
  },
  
  create: async (data) => {
    const response = await apiClient.post('/provisions', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await apiClient.put(`/provisions/${id}`, data);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await apiClient.delete(`/provisions/${id}`);
    return response.data;
  }
};

// ============ Campaign Provisions API (Extended) ============

export const campaignProvisionsAPI = {
  get: async (userId, campaignId) => {
    const response = await apiClient.get(`/campaign-provisions/${userId}/${campaignId}`);
    return response.data;
  },
  
  getMine: async (campaignId) => {
    const response = await apiClient.get(`/campaign-provisions/my/${campaignId}`);
    return response.data;
  },
  getMineAll: async (year) => {
    const response = await apiClient.get('/campaign-provisions/my-all', { params: year ? { year } : {} });
    return response.data;
  },
  
  getAll: async (year) => {
    const params = year ? { year } : {};
    const response = await apiClient.get('/campaign-provisions/all', { params });
    return response.data;
  },
  
  // Trainees
  addTrainee: async (userId, campaignId, data) => {
    const response = await apiClient.post(`/campaign-provisions/${userId}/${campaignId}/trainees`, data);
    return response.data;
  },
  
  updateTrainee: async (userId, campaignId, traineeId, data) => {
    const response = await apiClient.put(`/campaign-provisions/${userId}/${campaignId}/trainees/${traineeId}`, data);
    return response.data;
  },
  
  deleteTrainee: async (userId, campaignId, traineeId) => {
    const response = await apiClient.delete(`/campaign-provisions/${userId}/${campaignId}/trainees/${traineeId}`);
    return response.data;
  },
  
  // Advances (Vorschüsse)
  addAdvance: async (userId, campaignId, data) => {
    const response = await apiClient.post(`/campaign-provisions/${userId}/${campaignId}/advances`, data);
    return response.data;
  },
  
  deleteAdvance: async (userId, campaignId, advanceId) => {
    const response = await apiClient.delete(`/campaign-provisions/${userId}/${campaignId}/advances/${advanceId}`);
    return response.data;
  },
  
  // Payouts (Auszahlungen)
  addPayout: async (userId, campaignId, data) => {
    const response = await apiClient.post(`/campaign-provisions/${userId}/${campaignId}/payouts`, data);
    return response.data;
  },
  
  deletePayout: async (userId, campaignId, payoutId) => {
    const response = await apiClient.delete(`/campaign-provisions/${userId}/${campaignId}/payouts/${payoutId}`);
    return response.data;
  },
  
  // Multipliers
  getMultipliers: async (userId, campaignId) => {
    const response = await apiClient.get(`/campaign-provisions/${userId}/${campaignId}/multipliers`);
    return response.data;
  },
  
  updateMultipliers: async (userId, campaignId, multipliers) => {
    const response = await apiClient.put(`/campaign-provisions/${userId}/${campaignId}/multipliers`, multipliers);
    return response.data;
  },
  
  share: async (userId, campaignId, targetUserId) => {
    const response = await apiClient.post(`/campaign-provisions/${userId}/${campaignId}/share`, { target_user_id: targetUserId });
    return response.data;
  },
  
  // Trainer certificate update (for own trainees)
  updateMyCertificate: async (campaignId, traineeId, certificate, sendEmail = true) => {
    const response = await apiClient.put(`/campaign-provisions/my/${campaignId}/trainees/${traineeId}/certificate`, { certificate, send_email: sendEmail });
    return response.data;
  },
  
  // Trainer delete trainee (only for preparation trainees without units)
  deleteMyTrainee: async (campaignId, traineeId) => {
    const response = await apiClient.delete(`/campaign-provisions/my/${campaignId}/trainees/${traineeId}`);
    return response.data;
  }
};

// ============ Quarterly Provisions API (Neuwerber) ============

export const quarterlyProvisionsAPI = {
  get: async (userId, year, quarter) => {
    const response = await apiClient.get(`/quarterly-provisions/${userId}/${year}/${quarter}`);
    return response.data;
  },
  
  // Get my own quarterly provision (for trainers)
  getMine: async (year, quarter) => {
    const response = await apiClient.get(`/quarterly-provisions/my/${year}/${quarter}`);
    return response.data;
  },
  
  // Trainer certificate update (for own quarterly trainees)
  updateMyCertificate: async (year, quarter, traineeId, certificate) => {
    const response = await apiClient.put(`/quarterly-provisions/my/${year}/${quarter}/trainees/${traineeId}/certificate`, { certificate });
    return response.data;
  },
  
  // Trainees
  addTrainee: async (userId, year, quarter, data) => {
    const response = await apiClient.post(`/quarterly-provisions/${userId}/${year}/${quarter}/trainees`, data);
    return response.data;
  },
  
  updateTrainee: async (userId, year, quarter, traineeId, data) => {
    const response = await apiClient.put(`/quarterly-provisions/${userId}/${year}/${quarter}/trainees/${traineeId}`, data);
    return response.data;
  },
  
  deleteTrainee: async (userId, year, quarter, traineeId) => {
    const response = await apiClient.delete(`/quarterly-provisions/${userId}/${year}/${quarter}/trainees/${traineeId}`);
    return response.data;
  },
  
  // Multipliers
  getMultipliers: async (userId, year, quarter) => {
    const response = await apiClient.get(`/quarterly-provisions/${userId}/${year}/${quarter}/multipliers`);
    return response.data;
  },
  
  updateMultipliers: async (userId, year, quarter, multipliers) => {
    const response = await apiClient.put(`/quarterly-provisions/${userId}/${year}/${quarter}/multipliers`, multipliers);
    return response.data;
  },
  
  // Migration: Sync all campaign trainees to quarterly provisions
  migrateFromCampaigns: async () => {
    const response = await apiClient.post('/quarterly-provisions/migrate-from-campaigns');
    return response.data;
  },
  
  // Report PDF Download
  downloadReport: async (userId, year, quarter, trainerName) => {
    const response = await apiClient.get(`/quarterly-provisions/${userId}/${year}/${quarter}/report`, {
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Quartalsbericht_Q${quarter}_${year}_${trainerName.replace(/ /g, '_')}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return response.data;
  },
  
  // Invoice (Gutschrift) PDF Download
  downloadInvoice: async (userId, year, quarter, trainerName) => {
    const response = await apiClient.get(`/quarterly-provisions/${userId}/${year}/${quarter}/invoice`, {
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Gutschrift_Q${quarter}_${year}_${trainerName.replace(/ /g, '_')}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return response.data;
  }
};

// ============ OCR API ============

export const ocrAPI = {
  extractTrainees: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await apiClient.post('/ocr/extract-trainees', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }
};

// ============ Certificate API ============

export const certificateAPI = {
  generate: async (data) => {
    const response = await apiClient.post('/certificate/generate', {
      name: data.name,
      date_from: data.dateFrom ?? data.date_from,
      date_to: data.dateTo ?? data.date_to,
      topic: data.topic,
      notes: data.notes
    });
    return response.data;
  }
};

// ============ Tagesplanung Files API ============

export const tagesplanungAPI = {
  // Get all files
  getAllFiles: async () => {
    const response = await apiClient.get('/tagesplanung/files');
    return response.data;
  },
  
  // Get files for a specific day
  getFilesForDay: async (day) => {
    const response = await apiClient.get(`/tagesplanung/files/${day}`);
    return response.data;
  },
  
  // Upload file for a day (Admin only)
  uploadFile: async (day, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`/tagesplanung/files/${day}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },
  
  // Delete file (Admin only)
  deleteFile: async (day, fileId) => {
    const response = await apiClient.delete(`/tagesplanung/files/${day}/${fileId}`);
    return response.data;
  },
  
  // Get download URL
  getDownloadUrl: (day, fileId) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${baseUrl}/api/tagesplanung/files/${day}/${fileId}/download`;
  },
  
  // Get preview URL (for inline viewing)
  getPreviewUrl: (day, fileId) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${baseUrl}/api/tagesplanung/files/${day}/${fileId}/preview`;
  },
  
  // ============ Custom Content API ============
  
  // Get all custom content
  getAllContent: async () => {
    const response = await apiClient.get('/tagesplanung/content');
    return response.data;
  },
  
  // Get custom content for a day
  getContentForDay: async (day) => {
    const response = await apiClient.get(`/tagesplanung/content/${day}`);
    return response.data;
  },
  
  // Update custom content for a day (Admin only)
  updateContent: async (day, zeitbloecke) => {
    const response = await apiClient.put(`/tagesplanung/content/${day}`, { zeitbloecke });
    return response.data;
  },
  
  // Reset content to default (Admin only)
  resetContent: async (day) => {
    const response = await apiClient.delete(`/tagesplanung/content/${day}`);
    return response.data;
  }
};

export default apiClient;
